import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import { api } from './api'
import { useAbruf } from './Abruf'
import { HANDGRIFFE } from './Handgriffe'
import type { Handgriffname } from './Handgriffe'
import type { Handgriff } from './typen'

/**
 * Was der Anwender nicht darf, ist auf keiner Seite da (8.1 Abschnitt 7,
 * R1180–R1183).
 *
 * **Die Oberfläche fragt nur.** Welches Recht ein Handgriff braucht, sagt der
 * Server — aus seinen Endpunkten gelesen, an denen er es auch durchsetzt
 * (R1181, R361). Hier steht nur die Frage: *Darf der Angemeldete das?*
 *
 * **Das Weglassen schützt nichts** (R1183). Es räumt den Bildschirm auf: Ein
 * Prüfer, der nur lesen darf, sieht eine Leseseite und nicht zwanzig Knöpfe,
 * die ihn abweisen.
 */

export type Lage = {
  /** Die Rechte des Angemeldeten — `null` heißt: kein Rahmen, alles da. */
  rechte: readonly string[] | null
  /** Die Liste des Servers — `null`, solange sie unterwegs ist oder nicht kam. */
  liste: readonly Handgriff[] | null
  /**
   * Die Liste kam nicht (7.2 Abschnitt 39, R1210) — für den, der den Rahmen
   * stellt und am Ort „Ließ sich nicht laden" zeigen will.
   */
  ausgefallen?: boolean
  /** Holt die Liste neu — der Knopf *Erneut versuchen* (R1210). */
  erneut?: () => void
}

/*
  **Ohne Rahmen ist alles da** — im Prüffall, im Schaustück, in einem Fenster,
  das ohne Anmeldung gebaut wird. Dieselbe Ausfallstellung wie bei den
  Anzeigeoptionen: Wer nichts eingestellt hat, soll nichts vermissen; und
  geschützt wird ohnehin am Server (R1183).
*/
const Kontext = createContext<Lage>({ rechte: null, liste: null })

/**
 * Die Rechte und die Liste des Servers — für den, der den Rahmen stellt.
 *
 * **Warum ein eigener Hook:** Die Anwendung selbst lässt Übergaben weg, etwa
 * *Buchen* und *Anlegen* an den Berichten (R1180). Sie steht aber außerhalb des
 * Rahmens, den sie stellt, und fragt deshalb mit derselben Lage, die sie an ihn
 * weitergibt — eine Lage, nicht zwei.
 */
export function useRechtelage(rechte: readonly string[] | null): Lage {
  const angemeldet = rechte !== null

  /*
    **Ein Ausfall ist keine leere Liste** (7.2 Abschnitt 39, R1210, R1211).
    Bis zum 2026-10-06 wurde er zu `[]` — und eine leere Liste heißt in
    `erlaubt`: „kein Handgriff verlangt ein Recht". Ein Prüfer sah dann jeden
    schreibenden Knopf, den der Server ihm danach verweigerte (R1183).
    Seither bleibt die Liste `null` wie während des Ladens: Kein schreibender
    Knopf steht da, und der Ausfall ist festgehalten. Ein Hauptabruf, weil an
    ihm hängt, was die ganze Anwendung anbietet.
  */
  const abruf = useAbruf(angemeldet ? () => api.handgriffe() : null, [angemeldet])

  return { rechte, liste: abruf.wert, ausgefallen: abruf.ausgefallen, erneut: abruf.erneut }
}

/** Der Rahmen mit einer Lage, die sein Besitzer schon hält. */
export const Rechtekontext = Kontext.Provider

/** Der Rahmen um alles, was der Angemeldete sieht — einmal, oben in der Anwendung. */
export function Rechterahmen({ rechte, children }: { rechte: readonly string[]; children: ReactNode }) {
  return <Kontext.Provider value={useRechtelage(rechte)}>{children}</Kontext.Provider>
}

/** Ob eine Adresse des Servers (`/api/a/{x:long}/b`) eine Adresse der Oberfläche (`/api/a/{}/b`) meint. */
function trifft(server: string, oberflaeche: string): boolean {
  const s = server.split('/')
  const o = oberflaeche.split('/')
  return s.length === o.length && s.every((teil, i) => (teil.startsWith('{') ? o[i] === '{}' : teil === o[i]))
}

/** Ob ein Handgriff erlaubt ist — mit den Rechten und der Liste, wie sie gerade stehen. */
export function erlaubt(lage: Lage, name: Handgriffname): boolean {
  if (lage.rechte === null) {
    return true
  }

  // Solange die Liste unterwegs ist, steht kein schreibender Handgriff da —
  // ein Knopf, der aufblitzt und wieder verschwindet, wäre schlimmer.
  if (lage.liste === null) {
    return false
  }

  const [methode, adresse] = HANDGRIFFE[name].split(' ')
  const eintrag = lage.liste.find((h) => h.methode === methode && trifft(h.muster, adresse!))

  // Kein Eintrag heißt: Der Server verlangt kein Recht — angemeldet genügt.
  return eintrag === undefined || lage.rechte.includes(eintrag.verb)
}

/**
 * `darf('zuordnungStilllegen')` — ob der Angemeldete diesen Handgriff tun darf
 * (R1180). **Wer schreibt, fragt vorher** (R1182): Ein Prüffall meldet jede
 * Seite, die einen schreibenden Handgriff ruft, ohne hier gefragt zu haben.
 */
export function useDarf(): (name: Handgriffname) => boolean {
  const lage = useContext(Kontext)
  return (name) => erlaubt(lage, name)
}
